import { useState, useCallback } from "react";
import { useAxios } from "./useAxios";

interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

interface UploadResult {
  url: string;
  previewUrl: string;
}

/**
 * ファイルアップロードカスタムフック
 * Google Drive へのアップロード機能を提供
 */
export const useUpload = () => {
  const { axiosClient } = useAxios();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [error, setError] = useState<string | null>(null);

  const uploadImage = useCallback(
    async (file: File): Promise<UploadResult | null> => {
      // バリデーション
      const maxSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      if (!allowedTypes.includes(file.type)) {
        setError(
          "サポートされていない画像形式です。JPEG, PNG, GIF, WebP のみ対応しています。"
        );
        return null;
      }

      if (file.size > maxSize) {
        setError("ファイルサイズが大きすぎます。5MB以下にしてください。");
        return null;
      }

      setUploading(true);
      setError(null);
      setProgress(null);

      try {
        const formData = new FormData();
        formData.append("image", file);

        const response = await axiosClient.post<UploadResult>(
          "/api/upload/image",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent: any) => {
              const total = progressEvent.total || file.size;
              const loaded = progressEvent.loaded || 0;
              const percentage = Math.round((loaded / total) * 100);

              setProgress({
                loaded,
                total,
                percentage,
              });
            },
          }
        );

        return response.data;
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "アップロードに失敗しました";
        setError(errorMessage);
        return null;
      } finally {
        setUploading(false);
        setProgress(null);
      }
    },
    [axiosClient]
  );

  const uploadImages = useCallback(
    async (files: FileList | File[]): Promise<UploadResult[] | null> => {
      const fileArray: File[] = Array.isArray(files)
        ? (files as File[])
        : Array.from(files as FileList);
      const maxSize = 10 * 1024 * 1024; // 10MB
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
      ];

      // バリデーション
      for (const file of fileArray) {
        if (!allowedTypes.includes(file.type)) {
          setError("サポートされていない画像形式が含まれています");
          return null;
        }
        if (file.size > maxSize) {
          setError(
            "ファイルサイズが大きすぎる画像が含まれています（10MB以下）"
          );
          return null;
        }
      }

      setUploading(true);
      setError(null);
      setProgress(null);

      try {
        const formData = new FormData();
        fileArray.forEach((file: File) => formData.append("images", file));

        const response = await axiosClient.post<UploadResult[]>(
          "/api/upload/images",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (progressEvent: any) => {
              const total =
                progressEvent.total ||
                fileArray.reduce((s: number, f: File) => s + f.size, 0);
              const loaded = progressEvent.loaded || 0;
              const percentage = Math.round((loaded / total) * 100);
              setProgress({ loaded, total, percentage });
            },
          }
        );

        return response.data;
      } catch (err: any) {
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          "アップロードに失敗しました";
        setError(errorMessage);
        return null;
      } finally {
        setUploading(false);
        setProgress(null);
      }
    },
    [axiosClient]
  );

  return {
    uploadImage,
    uploadImages,
    uploading,
    progress,
    error,
  };
};
